import { useEffect, useState } from "react";
import "./spotlight.css";
import { BannerIcon } from "./bannerIcons";

/**
 * The Spotlight page banner — every section opens under one.
 *
 * Page glyph + page name in heavy capitals, the membership's name beneath in
 * spaced serif capitals, over artwork painted from the creator's brand colour.
 * See spotlight.css for the art.
 *
 * A creator can upload their own artwork per page (Brand -> Spotlight page
 * banners). When they have, the photo replaces the painted GROUND — the glow,
 * aurora, grid, beams and watermark all come off, because those compose against
 * flat dark and fight a photograph. What stays is the treatment that makes the
 * banner legible and recognisably Spotlight: the glass icon tile, the gradient
 * title, the serif subtitle after a brand rule, and an overlay dark enough to
 * keep white type readable on a bright image.
 *
 * Each section gets its own composition (angle, where the light starts, where
 * the lit edge falls), so moving between pages feels like turning pages rather
 * than reloading one banner.
 */

// Display faces for the banner. Loaded once, only when Spotlight renders.
let fontsRequested = false;
function ensureSpotlightFonts() {
  if (fontsRequested || typeof document === "undefined") return;
  fontsRequested = true;
  if (document.getElementById("spotlight-fonts")) return;
  const link = document.createElement("link");
  link.id = "spotlight-fonts";
  link.rel = "stylesheet";
  link.href =
    "https://fonts.googleapis.com/css2?family=Bodoni+Moda:opsz,wght@6..96,500;6..96,600&family=Montserrat:wght@800;900&display=swap";
  document.head.appendChild(link);
}

// Per-section composition: where the light sits (gx/gy), the beams' angle and
// crossings, and the aurora's tilt. Small moves, so the set reads as one family.
const COMPOSITION = {
  home: { gx: "80%", gy: "46%", angle: "116deg", b: ["56%", "63%", "71%"], rot: "-16deg" },
  courses: { gx: "84%", gy: "58%", angle: "122deg", b: ["60%", "67%", "76%"], rot: "-22deg" },
  groups: { gx: "78%", gy: "40%", angle: "110deg", b: ["54%", "62%", "69%"], rot: "-10deg" },
  feed: { gx: "78%", gy: "40%", angle: "110deg", b: ["54%", "62%", "69%"], rot: "-10deg" },
  events: { gx: "86%", gy: "52%", angle: "128deg", b: ["62%", "70%", "78%"], rot: "-26deg" },
  members: { gx: "76%", gy: "60%", angle: "114deg", b: ["55%", "64%", "72%"], rot: "-14deg" },
  map: { gx: "82%", gy: "36%", angle: "120deg", b: ["58%", "66%", "73%"], rot: "-20deg" },
  leaderboard: { gx: "80%", gy: "54%", angle: "112deg", b: ["57%", "65%", "74%"], rot: "-12deg" },
  rewards: { gx: "84%", gy: "44%", angle: "124deg", b: ["60%", "68%", "75%"], rot: "-24deg" },
  memberchat: { gx: "80%", gy: "50%", angle: "118deg", b: ["58%", "66%", "74%"], rot: "-18deg" },
  about: { gx: "78%", gy: "40%", angle: "110deg", b: ["54%", "62%", "69%"], rot: "-10deg" },
  pendingpost: { gx: "78%", gy: "40%", angle: "110deg", b: ["54%", "62%", "69%"], rot: "-10deg" },
  profile: { gx: "82%", gy: "48%", angle: "116deg", b: ["57%", "65%", "73%"], rot: "-16deg" },
  password: { gx: "80%", gy: "56%", angle: "120deg", b: ["59%", "66%", "74%"], rot: "-20deg" },
  plans: { gx: "84%", gy: "42%", angle: "112deg", b: ["56%", "64%", "72%"], rot: "-14deg" },
  page: { gx: "80%", gy: "50%", angle: "118deg", b: ["58%", "66%", "74%"], rot: "-18deg" },
  earnings: { gx: "86%", gy: "46%", angle: "126deg", b: ["61%", "69%", "77%"], rot: "-28deg" },
};

// Page names as the banner prints them — the nav's labels, which is what a
// member clicked to get here.
export const BANNER_TITLES = {
  home: "Home",
  courses: "Courses",
  groups: "Community",
  feed: "Community",
  events: "Events",
  members: "Members",
  map: "Map",
  leaderboard: "Leaderboard",
  rewards: "Rewards",
  memberchat: "Members Chat",
  about: "About",
  pendingpost: "Pending Posts",
  profile: "My Profile",
  password: "Password",
  plans: "My Plans",
  earnings: "My Earnings",
};

/**
 * The creator's uploaded artwork for a section, from brand_settings
 * (`spotlight_banners`), which reaches the app on the existing getfunnel ->
 * useBrand path. Falls back to their default banner, then to '' — which means
 * "paint the generated art", the behaviour every Spotlight membership has today.
 *
 * `feed` and `groups` are the same page to a member ("Community"), so they
 * share the groups entry rather than being configurable twice.
 */
export function spotlightBannerFor(brand, section) {
  const cfg = brand?.spotlight_banners;
  if (!cfg) return { image: "", overlay: "" };
  const key = section === "feed" ? "groups" : section;

  /*
   * Own string properties only. `pages` is an object, but older stored values
   * serialised an empty one as a JSON ARRAY — and `pages["map"]` on an Array is
   * Array.prototype.map, a function. `map` is a real section here, so a plain
   * lookup would hand a function back as the image URL.
   */
  const pages = cfg.pages;
  const own =
    pages && !Array.isArray(pages) && Object.prototype.hasOwnProperty.call(pages, key)
      ? pages[key]
      : "";

  const pick = (v) => (typeof v === "string" ? v : "");

  return {
    image: pick(own) || pick(cfg.default) || "",
    overlay: pick(cfg.overlay),
  };
}

export default function SpotlightBanner({
  section = "home",
  title,
  subtitle,
  primary = "#0274F7",
  image = "",
  overlay = "",
}) {
  ensureSpotlightFonts();
  const c = COMPOSITION[section] || COMPOSITION.home;
  const heading = title || BANNER_TITLES[section] || "Home";
  const hasImage = /^(https?:\/\/|\/\/|\/)/i.test(String(image || "").trim());

  /*
   * The painted art stays until the photo is CONFIRMED loaded.
   *
   * Swapping on the URL alone meant a broken link — or just a slow one — left
   * the banner as the flat dark ground with a black veil over it, which is
   * neither the creator's artwork nor their brand colour. Now anything short of
   * a loaded image falls back to the theme-coloured art, so the banner is never
   * a grey box.
   */
  const [photoReady, setPhotoReady] = useState(false);
  useEffect(() => {
    setPhotoReady(false);
    if (!hasImage) return undefined;
    const probe = new window.Image();
    let off = false;
    probe.onload = () => { if (!off) setPhotoReady(true); };
    probe.onerror = () => { if (!off) setPhotoReady(false); };
    probe.src = image;
    return () => { off = true; probe.onload = null; probe.onerror = null; };
  }, [image, hasImage]);

  const showPhoto = hasImage && photoReady;

  return (
    <section
      className={`sp-banner${showPhoto ? " sp-banner--photo" : ""}`}
      aria-label={heading}
      style={{
        "--sp": primary,
        "--gx": c.gx,
        "--gy": c.gy,
        "--sp-angle": c.angle,
        "--b1": c.b[0],
        "--b2": c.b[1],
        "--b3": c.b[2],
        "--rot": c.rot,
      }}
    >
      {showPhoto ? (
        <>
          {/* Its own element, never one already carrying a Tailwind position
              utility — see the note at the top of spotlight.css. */}
          <span
            className="sp-photo"
            aria-hidden="true"
            style={{ backgroundImage: `url("${String(image).replace(/"/g, "%22")}")` }}
          />
          <span
            className="sp-photo-veil"
            aria-hidden="true"
            style={{ background: overlay || "rgba(0,0,0,0.45)" }}
          />
        </>
      ) : (
        <>
          <span className="sp-glow" aria-hidden="true" />
          <span className="sp-aurora" aria-hidden="true" />
          <span className="sp-grid" aria-hidden="true" />
          <span className="sp-beams" aria-hidden="true" />
          <BannerIcon name={section} className="sp-watermark hidden sm:block" />
        </>
      )}
      <span className="sp-grain" aria-hidden="true" />
      <span className="sp-edge" aria-hidden="true" />

      <div className="flex h-[140px] items-center px-5 sm:h-[180px] sm:px-10 lg:h-[200px] lg:px-12">
        <div className="flex min-w-0 items-center gap-4 sm:gap-6">
          {/* The page glyph on a glass tile, lit from behind by the brand. */}
          <span
            className="grid h-14 w-14 shrink-0 place-items-center rounded-2xl bg-white/[0.08] ring-1 ring-inset ring-white/20 backdrop-blur-md sm:h-[76px] sm:w-[76px] sm:rounded-[22px]"
            style={{ boxShadow: `inset 0 1px 0 rgba(255,255,255,0.22), 0 18px 40px -18px ${primary}, 0 10px 30px -12px rgba(0,0,0,0.7)` }}
          >
            <BannerIcon name={section} className="h-7 w-7 text-white sm:h-10 sm:w-10" />
          </span>
          <div className="min-w-0">
            <h1 className="sp-banner-title">{heading}</h1>
            {subtitle ? (
              <p className="mt-2 flex min-w-0 items-center gap-3 sm:mt-3">
                <span className="h-px w-7 shrink-0 sm:w-12" style={{ background: `linear-gradient(90deg, ${primary}, transparent)` }} />
                <span className="sp-banner-sub truncate">{subtitle}</span>
              </p>
            ) : null}
          </div>
        </div>
      </div>
    </section>
  );
}
